import { memo, useCallback, useEffect, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Handle,
  NodeToolbar,
  Position,
  useReactFlow,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useNavigate } from 'react-router';
import Box from '@cloudscape-design/components/box';
import ELK from 'elkjs/lib/elk.bundled.js';
import type { RuleSummary, Severity } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../context/ArbiterClientContext';

export interface DependencyGraphProps {
  ruleName: string;
  severity?: Severity;
  parents: RuleSummary[];
  children: RuleSummary[];
}

const SEVERITY_COLORS: Record<Severity, { bg: string; border: string; text: string }> = {
  CRITICAL: { bg: '#4a1c1c', border: '#ef5350', text: '#ffcdd2' },
  HIGH: { bg: '#4a2c10', border: '#ff9800', text: '#ffe0b2' },
  MEDIUM: { bg: '#4a4010', border: '#ffca28', text: '#fff9c4' },
  LOW: { bg: '#1a3a1a', border: '#66bb6a', text: '#c8e6c9' },
};

/** Strip React Flow's default node wrapper styling so the custom component controls everything. */
const TRANSPARENT_WRAPPER: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  borderRadius: 0,
};

function relativeTime(value: Date | string | undefined): string {
  if (!value) return '--';
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  const diff = Date.now() - time;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Custom node with hover tooltip and theme-aware styling
const RuleNode = memo(({ data, id }: NodeProps) => {
  const [hovered, setHovered] = useState(false);
  const isCurrent = data.isCurrent as boolean;
  const severityColor = data.severityColor as { bg: string; border: string; text: string } | undefined;

  const isExpanded = data.isExpanded as boolean | undefined;
  const hiddenChildCount = (data.hiddenChildCount as number) ?? 0;
  const isLoading = data.isLoading as boolean | undefined;

  const description = data.description as string | undefined;
  const ruleStatus = data.ruleStatus as string | undefined;
  const version = data.version as string | undefined;
  const appliesTo = data.appliesTo as string[] | undefined;
  const lastModifiedAt = data.lastModifiedAt as Date | string | undefined;
  const severity = data.severity as string | undefined;

  const severityBadgeColor = severity
    ? SEVERITY_COLORS[severity as Severity]
    : undefined;

  const style: React.CSSProperties = isCurrent && severityColor
    ? {
        position: 'relative',
        background: severityColor.bg,
        border: `2px solid ${severityColor.border}`,
        color: severityColor.text,
        borderRadius: 8,
        padding: '10px 16px',
        fontWeight: 600,
        fontSize: 13,
        cursor: 'default',
      }
    : {
        position: 'relative',
        background: 'var(--color-background-container-content, #1a1f2b)',
        border: '1px solid var(--color-border-divider-default, #414d5c)',
        color: 'var(--color-text-body-default, #d1d5db)',
        borderRadius: 8,
        padding: '8px 14px',
        fontSize: 12,
        cursor: 'grab',
        boxShadow: hovered ? '0 0 0 2px rgba(9, 114, 211, 0.5)' : 'none',
        transition: 'box-shadow 0.15s ease',
      };

  const truncatedDescription = description
    ? description.length > 100
      ? `${description.slice(0, 100)}...`
      : description
    : undefined;

  return (
    <div
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <NodeToolbar isVisible={hovered && !isCurrent} position={Position.Top}>
        <div
          style={{
            background: 'var(--color-background-container-content, #1a1f2b)',
            border: '1px solid var(--color-border-divider-default, #414d5c)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 11,
            color: 'var(--color-text-body-default, #d1d5db)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            maxWidth: 280,
            minWidth: 180,
          }}
        >
          {/* Header: Rule name + severity badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 12 }}>{id}</span>
            {severity && severityBadgeColor && (
              <span
                style={{
                  background: severityBadgeColor.bg,
                  color: severityBadgeColor.text,
                  border: `1px solid ${severityBadgeColor.border}`,
                  borderRadius: 4,
                  padding: '1px 6px',
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                {severity}
              </span>
            )}
          </div>

          {/* Description */}
          {truncatedDescription && (
            <div
              style={{
                color: 'var(--color-text-body-secondary, #8d99ae)',
                fontSize: 11,
                lineHeight: 1.4,
                marginBottom: 8,
              }}
            >
              {truncatedDescription}
            </div>
          )}

          {/* 2x2 detail grid */}
          {(ruleStatus || version || appliesTo || lastModifiedAt) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '4px 12px',
                fontSize: 10,
                marginBottom: 8,
                borderTop: '1px solid var(--color-border-divider-default, #414d5c)',
                paddingTop: 6,
              }}
            >
              {ruleStatus && (
                <div>
                  <div style={{ color: 'var(--color-text-body-secondary, #8d99ae)' }}>Status</div>
                  <div style={{ fontWeight: 600 }}>{ruleStatus}</div>
                </div>
              )}
              {version && (
                <div>
                  <div style={{ color: 'var(--color-text-body-secondary, #8d99ae)' }}>Version</div>
                  <div style={{ fontWeight: 600 }}>{version}</div>
                </div>
              )}
              {appliesTo && appliesTo.length > 0 && (
                <div>
                  <div style={{ color: 'var(--color-text-body-secondary, #8d99ae)' }}>Applies To</div>
                  <div style={{ fontWeight: 600 }}>{appliesTo.join(', ')}</div>
                </div>
              )}
              {lastModifiedAt && (
                <div>
                  <div style={{ color: 'var(--color-text-body-secondary, #8d99ae)' }}>Last Modified</div>
                  <div style={{ fontWeight: 600 }}>{relativeTime(lastModifiedAt)}</div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              color: 'var(--color-text-body-secondary, #8d99ae)',
              fontSize: 10,
              fontStyle: 'italic',
            }}
          >
            Double-click to open &rarr;
          </div>
        </div>
      </NodeToolbar>
      <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
      {data.label as string}

      {/* Collapse badge showing hidden children count (D-04) */}
      {!isCurrent && !isExpanded && hiddenChildCount > 0 && (
        <span
          style={{
            position: 'absolute',
            bottom: -8,
            right: -8,
            background: '#0972d3',
            color: '#fff',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          +{hiddenChildCount}
        </span>
      )}

      {/* Loading indicator while fetching children */}
      {isLoading && (
        <span
          style={{
            position: 'absolute',
            bottom: -8,
            right: -8,
            fontSize: 10,
            color: 'var(--color-text-body-secondary, #8d99ae)',
          }}
        >
          ...
        </span>
      )}

      <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
    </div>
  );
});

RuleNode.displayName = 'RuleNode';

const nodeTypes = { ruleNode: RuleNode };

const elk = new ELK();

async function layoutGraph(
  nodes: Node[],
  edges: Edge[],
): Promise<Node[]> {
  const elkGraph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'DOWN',
      'elk.spacing.nodeNode': '80',
      'elk.layered.spacing.nodeNodeBetweenLayers': '100',
    },
    children: nodes.map((node) => ({
      id: node.id,
      width: Math.max(node.id.length * 8 + 32, 120),
      height: 40,
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  const layout = await elk.layout(elkGraph);

  return nodes.map((node) => {
    const elkNode = layout.children?.find((n: { id: string }) => n.id === node.id);
    return {
      ...node,
      position: {
        x: elkNode?.x ?? 0,
        y: elkNode?.y ?? 0,
      },
    };
  });
}

function buildGraphData(
  ruleName: string,
  severity: Severity | undefined,
  parents: RuleSummary[],
  children: RuleSummary[],
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const severityColor = severity ? SEVERITY_COLORS[severity] : undefined;

  // Current rule node — wrapper is transparent, custom component handles styling
  nodes.push({
    id: ruleName,
    position: { x: 0, y: 0 },
    type: 'ruleNode',
    style: TRANSPARENT_WRAPPER,
    data: { label: ruleName, isCurrent: true, severityColor },
  });

  // Parent nodes — tooltip data comes from RuleSummary on first render
  parents.forEach((parent) => {
    nodes.push({
      id: parent.ruleName!,
      position: { x: 0, y: 0 },
      type: 'ruleNode',
      style: TRANSPARENT_WRAPPER,
      data: {
        label: parent.ruleName!,
        isCurrent: false,
        description: parent.description,
        ruleStatus: parent.ruleStatus,
        version: parent.version,
        appliesTo: parent.appliesTo,
        lastModifiedAt: parent.lastModifiedAt,
        severity: parent.severity,
      },
    });
    edges.push({
      id: `${parent.ruleName!}->${ruleName}`,
      source: parent.ruleName!,
      target: ruleName,
      style: { stroke: 'var(--color-border-divider-default, #414d5c)', strokeWidth: 1.5 },
    });
  });

  // Child nodes — tooltip data comes from RuleSummary on first render
  children.forEach((child) => {
    nodes.push({
      id: child.ruleName!,
      position: { x: 0, y: 0 },
      type: 'ruleNode',
      style: TRANSPARENT_WRAPPER,
      data: {
        label: child.ruleName!,
        isCurrent: false,
        description: child.description,
        ruleStatus: child.ruleStatus,
        version: child.version,
        appliesTo: child.appliesTo,
        lastModifiedAt: child.lastModifiedAt,
        severity: child.severity,
      },
    });
    edges.push({
      id: `${ruleName}->${child.ruleName!}`,
      source: ruleName,
      target: child.ruleName!,
      style: { stroke: 'var(--color-border-divider-default, #414d5c)', strokeWidth: 1.5 },
    });
  });

  return { nodes, edges };
}

function DependencyGraphInner({ ruleName, severity, parents, children }: DependencyGraphProps) {
  const navigate = useNavigate();
  const { fitView } = useReactFlow();
  const client = useArbiterClient();
  const hasRelations = parents.length > 0 || children.length > 0;

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);

  // Track which nodes are currently expanded
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  // Cache fetched rule data to avoid re-fetching on re-expand
  const [childrenCache, setChildrenCache] = useState<Map<string, { parents: RuleSummary[]; children: RuleSummary[] }>>(new Map());
  // Track nodes currently being fetched (loading indicator)
  const [, setLoadingNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!hasRelations) return;

    const { nodes: rawNodes, edges: rawEdges } = buildGraphData(ruleName, severity, parents, children);

    layoutGraph(rawNodes, rawEdges).then((layoutedNodes) => {
      setNodes(layoutedNodes);
      setEdges(rawEdges);
      requestAnimationFrame(() => fitView({ padding: 0.3 }));
    });
  }, [ruleName, severity, parents, children, hasRelations, fitView]);

  // Expand/collapse toggle for non-current nodes
  const toggleExpand = useCallback(async (nodeId: string) => {
    // D-06: Current node is not expandable
    if (nodeId === ruleName) return;

    const isExpanded = expandedNodes.has(nodeId);

    if (isExpanded) {
      // COLLAPSE: Remove children that were added by expanding this node
      const cachedStructure = childrenCache.get(nodeId);
      if (!cachedStructure) return;

      const childIds = new Set(cachedStructure.children.map((c) => c.ruleName!));

      // Only remove child nodes that have no OTHER expanded parent
      const remainingNodes = nodes.filter((n) => {
        if (!childIds.has(n.id)) return true;
        for (const [parentId, parentStructure] of childrenCache.entries()) {
          if (parentId !== nodeId && expandedNodes.has(parentId) && parentStructure.children.some((c) => c.ruleName === n.id)) {
            return true;
          }
        }
        return false;
      }).map((n) => {
        // Update the collapsed node to show badge with hidden child count
        if (n.id === nodeId) {
          return { ...n, data: { ...n.data, isExpanded: false, hiddenChildCount: cachedStructure.children.length } };
        }
        return n;
      });

      const remainingEdges = edges.filter((e) => {
        if (e.source !== nodeId) return true;
        return !childIds.has(e.target);
      });

      setExpandedNodes((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
      setEdges(remainingEdges);

      // Re-layout after collapse
      const sortedNodes = [...remainingNodes].sort((a, b) => a.id.localeCompare(b.id));
      const layouted = await layoutGraph(sortedNodes, remainingEdges);
      setNodes(layouted);
      requestAnimationFrame(() => fitView({ padding: 0.3, duration: 300 }));
    } else {
      // EXPAND: Fetch children and add to graph
      let cached = childrenCache.get(nodeId);
      if (!cached) {
        // Show loading state on node
        setLoadingNodes((prev) => new Set(prev).add(nodeId));
        setNodes((prev) => prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, isLoading: true } } : n));
        try {
          const response = await client.describeRule({ ruleName: nodeId, includeDependencies: true });
          cached = { parents: response.parents ?? [], children: response.children ?? [] };
          setChildrenCache((prev) => new Map(prev).set(nodeId, cached!));
        } catch {
          // If fetch fails, silently skip expand
          setLoadingNodes((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
          setNodes((prev) => prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, isLoading: false } } : n));
          return;
        }
        setLoadingNodes((prev) => { const next = new Set(prev); next.delete(nodeId); return next; });
        setNodes((prev) => prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, isLoading: false } } : n));
      }

      // Add child nodes (skip duplicates -- circular dependency guard)
      const existingNodeIds = new Set(nodes.map((n) => n.id));
      const newNodes: Node[] = [];
      const newEdges: Edge[] = [];

      for (const childSummary of cached.children) {
        const childName = childSummary.ruleName!;
        if (!existingNodeIds.has(childName)) {
          newNodes.push({
            id: childName,
            position: { x: 0, y: 0 },
            type: 'ruleNode',
            style: TRANSPARENT_WRAPPER,
            data: {
              label: childName,
              isCurrent: false,
              description: childSummary.description,
              ruleStatus: childSummary.ruleStatus,
              version: childSummary.version,
              appliesTo: childSummary.appliesTo,
              lastModifiedAt: childSummary.lastModifiedAt,
              severity: childSummary.severity,
            },
          });
          existingNodeIds.add(childName);
        }
        const edgeId = `${nodeId}->${childName}`;
        const edgeExists = edges.some((e) => e.id === edgeId);
        if (!edgeExists) {
          newEdges.push({
            id: edgeId,
            source: nodeId,
            target: childName,
            style: { stroke: 'var(--color-border-divider-default, #414d5c)', strokeWidth: 1.5 },
          });
        }
      }

      // Merge new nodes and edges, re-layout, and mark as expanded
      const allNodes = [...nodes, ...newNodes].map((n) => {
        if (n.id === nodeId) {
          return { ...n, data: { ...n.data, isExpanded: true, hiddenChildCount: 0 } };
        }
        return n;
      });
      const allEdges = [...edges, ...newEdges];

      setExpandedNodes((prev) => new Set(prev).add(nodeId));
      setEdges(allEdges);

      // Re-layout with ELK after topology change
      const sortedNodes = [...allNodes].sort((a, b) => a.id.localeCompare(b.id));
      const layouted = await layoutGraph(sortedNodes, allEdges);
      setNodes(layouted);
      requestAnimationFrame(() => fitView({ padding: 0.3, duration: 300 }));
    }
  }, [expandedNodes, childrenCache, nodes, edges, ruleName, fitView, client]);

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      // D-06: Current node is not expandable
      if (node.id === ruleName) return;
      toggleExpand(node.id);
    },
    [ruleName, toggleExpand],
  );

  const onNodeDoubleClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (node.id !== ruleName) {
        navigate(`/rules/${encodeURIComponent(node.id)}`);
      }
    },
    [ruleName, navigate],
  );

  if (!hasRelations) {
    return (
      <Box textAlign="center" color="text-body-secondary" padding="l">
        This rule has no dependencies
      </Box>
    );
  }

  return (
    <div style={{ height: 500 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        proOptions={{ hideAttribution: true }}
      >
      </ReactFlow>
    </div>
  );
}

export function DependencyGraph(props: DependencyGraphProps) {
  return (
    <ReactFlowProvider>
      <DependencyGraphInner {...props} />
    </ReactFlowProvider>
  );
}
